import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Chip, CircularProgress, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import StarIcon from "@mui/icons-material/Star";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import {
  VendorProfileIntelligence, VendorInsight,
  fetchVendorProfileIntelligence,
} from "./vendorIntelligenceApi";

/* ── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  navy: "#0d1b2a", gold: "#c9a96e", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f7f5f1",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee",
  red: "#9b2c2c", redBg: "#fef2f2",
  blue: "#1e40af", blueBg: "#eff6ff",
  purple: "#6d28d9", purpleBg: "#f5f3ff",
  orange: "#c2410c", orangeBg: "#fff7ed",
};

const fmtCAD = (n: number) =>
  `$${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INSIGHT_ICONS: Record<string, React.ReactNode> = {
  positive:   <CheckCircleOutlineIcon sx={{ fontSize: 16, color: T.green }} />,
  warning:    <WarningAmberIcon sx={{ fontSize: 16, color: T.orange }} />,
  info:       <InfoOutlinedIcon sx={{ fontSize: 16, color: T.blue }} />,
  suggestion: <LightbulbOutlinedIcon sx={{ fontSize: 16, color: T.purple }} />,
  danger:     <WarningAmberIcon sx={{ fontSize: 16, color: T.red }} />,
};

const INSIGHT_BG: Record<string, string> = {
  positive: T.greenBg, warning: T.orangeBg, info: T.blueBg,
  suggestion: T.purpleBg, danger: T.redBg,
};

const INSIGHT_TX: Record<string, string> = {
  positive: T.green, warning: T.orange, info: T.blue,
  suggestion: T.purple, danger: T.red,
};

/* ── Summary Card ─────────────────────────────────────────────────────── */
const SummaryCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; bg: string;
}> = ({ label, value, icon, color, bg }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 1.5, p: 1.5,
    borderRadius: 2.5, bgcolor: T.white, border: `1px solid ${T.line}`,
    transition: "all 0.15s", "&:hover": { borderColor: T.gold },
  }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 2, bgcolor: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 18, color } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 9, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR PROFILE INTELLIGENCE PANEL
   Shows intelligence summary, insights, top items, and recent events
   ═══════════════════════════════════════════════════════════════════════════ */
interface Props {
  vendorId: number;
}

const VendorProfileIntelligencePanel: React.FC<Props> = ({ vendorId }) => {
  const [data, setData] = useState<VendorProfileIntelligence | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchVendorProfileIntelligence(vendorId);
      setData(d);
    } catch (e) { console.error("Profile intelligence error:", e); }
    setLoading(false);
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3, border: `1px solid ${T.line}`, textAlign: "center" }}>
        <CircularProgress size={28} sx={{ color: T.gold }} />
        <Typography sx={{ fontSize: 12, color: T.slate, mt: 1 }}>Loading vendor intelligence...</Typography>
      </Paper>
    );
  }

  if (!data) return null;

  const s = data.summary;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <InsightsOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
            Vendor Intelligence Summary
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
          <SummaryCard label="Services Provided" value={String(s.services_provided)}
            icon={<CategoryOutlinedIcon />} color={T.blue} bg={T.blueBg} />
          <SummaryCard label="Avg Margin" value={`${s.avg_margin}%`}
            icon={<PercentOutlinedIcon />} color={s.avg_margin >= 30 ? T.green : T.red} bg={s.avg_margin >= 30 ? T.greenBg : T.redBg} />
          <SummaryCard label="Total Jobs" value={String(s.total_jobs)}
            icon={<WorkOutlineIcon />} color={T.navy} bg={T.cream} />
          <SummaryCard label="Total Revenue" value={fmtCAD(s.total_revenue)}
            icon={<MonetizationOnOutlinedIcon />} color={T.blue} bg={T.blueBg} />
          <SummaryCard label="Total Profit" value={fmtCAD(s.total_profit)}
            icon={<TrendingUpIcon />} color={T.green} bg={T.greenBg} />
          <SummaryCard label="Recommendation Score" value={`${s.recommendation_score}%`}
            icon={<EmojiEventsOutlinedIcon />} color={T.gold} bg={T.goldLight} />
          <SummaryCard label="Best Category" value={s.best_category || "N/A"}
            icon={<AutoFixHighOutlinedIcon />} color={T.purple} bg={T.purpleBg} />
          <SummaryCard label="Admin Rating" value={`${s.admin_rating}/5`}
            icon={<StarIcon />} color={T.gold} bg={T.goldLight} />
        </Box>
      </Paper>

      {/* ── Smart Insights ─────────────────────────────────────────────── */}
      {data.insights.length > 0 && (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LightbulbOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
              Smart Insights
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.insights.map((insight, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "flex-start", gap: 1.2, p: 1.5,
                borderRadius: 2.5, bgcolor: INSIGHT_BG[insight.type] || T.cream,
                border: `1px solid ${T.line}`,
              }}>
                {INSIGHT_ICONS[insight.type]}
                <Typography sx={{ fontSize: 12, color: INSIGHT_TX[insight.type] || T.slate, lineHeight: 1.5, fontWeight: 500 }}>
                  {insight.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Top Profitable Items ───────────────────────────────────────── */}
      {data.top_items.length > 0 && (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, mb: 2 }}>
            Most Profitable Items
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.top_items.map((item, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 1.5, borderRadius: 2, bgcolor: i < 3 ? T.cream : T.white,
                border: `1px solid ${T.line}`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "50%", fontSize: 10, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: i < 3 ? T.gold : T.slateLt, color: T.white,
                  }}>
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{item.name}</Typography>
                    <Typography sx={{ fontSize: 10, color: T.slateLt }}>Used {item.count}x</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.green }}>{fmtCAD(item.profit)}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Recent Event Usage ──────────────────────────────────────────── */}
      {data.recent_events.length > 0 && (
        <Paper sx={{
          borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}>
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
              Recent Event Usage
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: T.cream }}>
                  {["Item", "Event Type", "Date", "Revenue", "Cost", "Profit", "Margin"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: T.navy, py: 1.2, borderColor: T.line }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recent_events.map((ev, i) => (
                  <TableRow key={i} sx={{ "&:hover": { bgcolor: T.cream } }}>
                    <TableCell sx={{ fontSize: 11, fontWeight: 600, color: T.navy, borderColor: T.line }}>{ev.item_name}</TableCell>
                    <TableCell sx={{ borderColor: T.line }}>
                      <Chip label={ev.event_type || "N/A"} size="small" sx={{
                        height: 20, fontSize: 9, fontWeight: 600, bgcolor: T.blueBg, color: T.blue,
                      }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: T.slate, borderColor: T.line }}>{ev.event_date || "N/A"}</TableCell>
                    <TableCell sx={{ fontSize: 11, borderColor: T.line }}>{fmtCAD(ev.revenue)}</TableCell>
                    <TableCell sx={{ fontSize: 11, borderColor: T.line }}>{fmtCAD(ev.cost)}</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700, color: ev.profit >= 0 ? T.green : T.red, borderColor: T.line }}>
                      {fmtCAD(ev.profit)}
                    </TableCell>
                    <TableCell sx={{ borderColor: T.line }}>
                      <Chip label={`${ev.margin}%`} size="small" sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: ev.margin >= 40 ? T.greenBg : ev.margin >= 20 ? T.goldLight : T.redBg,
                        color: ev.margin >= 40 ? T.green : ev.margin >= 20 ? "#8b6914" : T.red,
                      }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default VendorProfileIntelligencePanel;
